import { Controller } from '@hotwired/stimulus';
import { Turbo } from '@hotwired/turbo-rails';
import { open } from '../lib/maplibre';
import { esc } from '../lib/text';

// MapLibre is its own chunk, fetched by the first map that connects (docs/MAPS.md § The controller).
let maplibregl;

// The one map (docs/MAPS.md § The controller). Pages differ only in values:
//
//   radar     — many things, and a `frame` to refresh as you pan
//   one thing — no frame, so the map just sits there
//
// What it draws is a matter of zoom, and the server already decided: it
// sends `pin` targets (one point, a flyer and all) or `spot` targets (a city
// or a venue, with a count). Both carry their JSON in `data-point`, so the
// same controller draws every scale and knows nothing about any of them.
//
// A spot is a door: clicking one flies to the zoom it names, the pan reloads
// the frame, and the next scale down is what comes back.
//
// With `clouds` the map is big on top, small like glow (docs/MAPS.md § Fun
// clouds): the loudest few in view are flyers, every pin is a point of one
// heatmap, and close in the glow clears and each is its own pin. A host that
// covers the canvas's foot says so (`pad`), and the middle moves up.
export default class extends Controller {
  static targets = ['canvas', 'pin', 'spot'];
  static values = {
    center: Array,
    zoom: { type: Number, default: 14 },
    // Empty: the quiet basemap for whichever theme the page is wearing.
    style: String,
    // A Mapbox pk., for a mapbox:// style. Empty: Mapbox is never asked.
    token: String,
    // A turbo frame to reload with the new bounds on pan. Empty: a still map.
    frame: String,
    // Frame the pins drawn, again whenever the list swaps them.
    fit: Boolean,
    // The opening view (center, zoom) is the one asked for: the first fit is skipped.
    keep: Boolean,
    // The loudest few in view are flyers over a glow; the rest are the glow.
    clouds: Boolean,
    // A door out: the fullscreen button becomes a link here, carrying the
    // page's query and the map's center and zoom. Unset: fullscreen.
    expand: String,
    expandLabel: String,
    // Sometimes a radar sweep turns once over the map (an easter egg).
    sweep: Boolean,
  };

  // Flyers in a screen: the loudest few. More than eight is a wall, and the
  // owner asked for few and loud. ponytail: a constant, not a value.
  static TOP = 8;
  // Of those, the loudest three are bigger: two sizes, never a scale.
  static BIG = 3;
  // From here every party in view is its own pin and the glow is gone.
  static STREET = 15;
  // Room kept clear at the sides: half a popup, and a pill at 200% text. The fit leaves it, a flyer needs it.
  static EDGE = 180;
  // A pin shrinking into its spot, or out of it, in this many ms.
  static FADE = 200;
  // One load in ten the green sweep turns. ponytail: the odds are a constant.
  static SWEEP_ODDS = 0.1;

  // For when the browser won't say and the server didn't either: no city in
  // the JS, the same point Findable::FALLBACK_GEOM draws (docs/MAPS.md).
  static NOWHERE = [0, 0];

  // Liberty painted every park green and every road orange, and the pins
  // drowned in it. These two are grey on purpose: the map is the ground,
  // the pins are what's on it. OpenFreeMap: no key, no cookie.
  static TILES = {
    light: 'https://tiles.openfreemap.org/styles/positron',
    dark: 'https://tiles.openfreemap.org/styles/dark',
  };

  // Quieter still: building footprints, footpaths, rails, road shields,
  // one-way arrows and the small streets' names, off (positron's and dark's
  // layer ids). Main roads, water and place names stay to find your way by.
  static QUIET =
    /^(building|highway[-_]path|highway-name-(minor|path)|highway-shield.*|road_shield.*|road_oneway.*|railway.*)$/;

  // MapLibre fetches only http(s); Mapbox GL's own shorthand, spelled out.
  static MAPBOX = [
    [/^mapbox:\/\/styles\//, 'https://api.mapbox.com/styles/v1/'],
    [/^mapbox:\/\/sprites\/([^/]+\/[^/@.]+)/, 'https://api.mapbox.com/styles/v1/$1/sprite'],
    [/^mapbox:\/\/fonts\//, 'https://api.mapbox.com/fonts/v1/'],
    [/^mapbox:\/\/([^?]+)$/, 'https://api.mapbox.com/v4/$1.json?secure'],
  ];

  map = null;
  markers = new Map();
  pins = new Map();
  // px of the canvas's foot a host's sheet covers (`pad`, docs/MAPS.md § Padding).
  covered = 0;
  // The opening view is the one asked for (`keep`): no fit until the map has settled.
  kept = this.keepValue;

  async connect() {
    maplibregl = await import('maplibre-gl');
    if (!this.hasCenterValue || this.centerValue.length !== 2) {
      this.centerValue = await this.locate();
    }
    // Gone while the chunk or the position was on its way: nothing to draw on.
    if (!this.element.isConnected) return;

    this.map = open(maplibregl, {
      container: this.canvasTarget,
      style: this.tiles(),
      center: this.centerValue,
      zoom: this.zoomValue,
      attributionControl: false,
      transformRequest: this.tokenValue ? (url) => this.mapbox(url) : undefined,
    });
    // No WebGL2: the list is the page, and a blank box is not a map.
    if (!this.map) {
      this.canvasTarget.hidden = true;
      return;
    }
    if (this.covered) this.pad({ detail: { bottom: this.covered } });
    this.map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');
    this.map.addControl(this.expandValue ? this.expander() : new maplibregl.FullscreenControl());
    // Bottom right is where a host's chat bubble floats.
    this.map.addControl(new maplibregl.AttributionControl({ compact: true }), 'bottom-left');
    this.collapseCredit();
    this.map.on('style.load', () => this.quiet());
    this.map.on('move', () => this.spread());
    this.map.on('load', () => {
      if (this.cloudsValue) this.showPins();
      else this.drawAll();
      // The opening view stays: no fit from the first frames, which only name the pins.
      if (this.keepValue) requestAnimationFrame(() => requestAnimationFrame(() => (this.kept = false)));
      else this.refit();
      // docs/MAPS.md § Ready: what a spec waits on.
      this.element.dataset.mapReady = 'true';
      if (this.sweepValue && !this.still() && Math.random() < this.constructor.SWEEP_ODDS) this.sweepOnce();
    });
    // The markers are DOM and stay put through a new style; the glow goes in again with it.
    this.map.on('style.load', () => this.cloudsValue && this.mountClouds());
    if (this.cloudsValue) {
      this.map.on('moveend', () => this.showPins());
    }
    // Born hidden (a phone's Lista|Mapa switch), it had no size to fit to.
    // A resize or a rotation ends in a moveend that is no pan (`hold`).
    this.map.on('resize', () => {
      this.hold();
      if (this.unfit) this.refit();
      if (this.cloudsValue) this.showPins();
    });
    if (this.frameValue) {
      this.map.on('moveend', () => {
        // The opening fitBounds is not a pan: don't reload what we just framed.
        if (this.holding) return;
        this.refresh();
      });
    }

    // The theme switch flips data-theme on <html>; the tiles follow. Markers
    // are DOM, so a new style leaves them where they are.
    this.theme = new MutationObserver(() => this.map?.setStyle(this.tiles()));
    this.theme.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme'],
    });
  }

  // MapLibre opens the credit the moment the style names its sources, and
  // only a drag folds it: on a 16rem phone map that is 40px of text over the
  // ground until the first touch. Fold it once, when it first appears; the "i"
  // still toggles it (OSM's ODbL needs it reachable, not shown).
  collapseCredit() {
    const fold = () => {
      const credit = this.element.querySelector('.maplibregl-ctrl-attrib.maplibregl-compact');
      if (!credit) return;
      credit.classList.remove('maplibregl-compact-show');
      this.map.off('sourcedata', fold);
    };
    this.map.on('sourcedata', fold);
  }

  // A host's sheet covers the canvas's foot by `detail.bottom` px (qir's phone
  // drawer says `drawer:peek`): the pan, a flight and the next reload's centre
  // are the middle of what is left. Not a pan: the frame is the one asked for.
  pad({ detail: { bottom } }) {
    this.covered = bottom;
    if (!this.map) return;
    const was = this.holding;
    this.holding = true; // `setPadding` ends its move before it returns
    this.map.setPadding({ top: 0, left: 0, right: 0, bottom });
    this.holding = was;
    if (this.cloudsValue) this.showPins();
  }

  // The mini map's door (docs/MAPS.md § Expand): a link control in the
  // fullscreen button's place, same glyph.
  expander() {
    const group = document.createElement('div');
    group.className = 'maplibregl-ctrl maplibregl-ctrl-group';
    const link = document.createElement('button');
    link.type = 'button';
    link.setAttribute('role', 'link');
    link.className = 'maplibregl-ctrl-fullscreen';
    link.title = link.ariaLabel = this.expandLabelValue;
    const icon = document.createElement('span');
    icon.className = 'maplibregl-ctrl-icon';
    icon.setAttribute('aria-hidden', 'true');
    link.append(icon);
    link.addEventListener('click', () => Turbo.visit(this.door()));
    group.append(link);
    return { onAdd: () => group, onRemove: () => group.remove() };
  }

  // The expand url, the page's own query (its filters) and the map's view.
  door() {
    const url = new URL(this.expandValue, window.location.origin);
    for (const [key, value] of new URLSearchParams(window.location.search)) url.searchParams.set(key, value);
    const { lng, lat } = this.map.getCenter();
    url.searchParams.set('lng', lng.toFixed(3));
    url.searchParams.set('lat', lat.toFixed(3));
    url.searchParams.set('zoom', Math.round(this.map.getZoom()));
    return url.toString();
  }

  tiles() {
    if (this.styleValue) return this.styleValue;
    const scheme = getComputedStyle(document.documentElement).colorScheme;
    return this.constructor.TILES[scheme === 'light' ? 'light' : 'dark'];
  }

  // Ours only: a host's own style is drawn as its author meant.
  quiet() {
    if (this.styleValue) return;
    for (const { id } of this.map.getStyle().layers) {
      if (this.constructor.QUIET.test(id)) this.map.setLayoutProperty(id, 'visibility', 'none');
    }
    // Service roads (parking aisles, rail yards) hatch whole blocks grey; the streets stay.
    if (this.map.getLayer('highway_minor')) {
      this.map.setFilter('highway_minor', [
        'all',
        this.map.getFilter('highway_minor'),
        ['!=', ['get', 'class'], 'service'],
      ]);
    }
    // Dark names every street in one layer; the small ones go, as positron's highway-name-minor does.
    if (this.map.getLayer('highway_name_other')) {
      this.map.setFilter('highway_name_other', [
        'all',
        this.map.getFilter('highway_name_other'),
        ['match', ['get', 'class'], ['minor', 'service', 'track'], false, true],
      ]);
    }
  }

  // Every ask to Mapbox carries the pk.; nobody else's does.
  mapbox(url) {
    const https = this.constructor.MAPBOX.reduce((at, [from, to]) => at.replace(from, to), url);
    if (!https.startsWith('https://api.mapbox.com/')) return;

    const signed = new URL(https);
    signed.searchParams.set('access_token', this.tokenValue);
    return { url: signed.toString() };
  }

  disconnect() {
    this.theme?.disconnect();
    this.element.dataset.mapReady = 'false';
    this.map?.remove();
    this.map = null;
    this.markers.clear();
    this.pins.clear();
    this.spot = null;
  }

  // Turbo swaps the list as you pan, so what is drawn follows the targets in
  // and out rather than being redrawn wholesale.
  pinTargetConnected(target) {
    if (this.cloudsValue) return this.track(target);
    if (this.map?.loaded()) this.draw(target, false);
    this.refit();
  }

  spotTargetConnected(target) {
    if (this.map?.loaded()) this.draw(target, true);
  }

  pinTargetDisconnected(target) {
    if (this.cloudsValue) {
      this.pins.delete(String(this.point(target).id));
      this.queuePins();
      return;
    }
    this.erase(target);
    this.lit?.remove();
    this.refit();
  }

  spotTargetDisconnected(target) {
    this.erase(target);
    this.spread();
  }

  drawAll() {
    this.pinTargets.forEach((one) => this.draw(one, false));
    this.spotTargets.forEach((one) => this.draw(one, true));
  }

  // One frame after the last target moved, so a swapped list fits once.
  refit() {
    if (!this.fitValue || this.kept || !this.map?.loaded()) return;
    cancelAnimationFrame(this.fitting);
    this.fitting = requestAnimationFrame(() => {
      const points = this.pinTargets.map((one) => this.point(one).geom).filter(Boolean);
      this.unfit = !this.canvasTarget.clientWidth;
      if (!points.length || this.unfit) return;

      const bounds = points.reduce((all, at) => all.extend(at), new maplibregl.LngLatBounds(points[0], points[0]));
      // Half a popup of room on each side: its tip stays centred (hover).
      const side = Math.min(this.constructor.EDGE, this.canvasTarget.clientWidth / 4);
      this.hold(() =>
        this.map.fitBounds(bounds, {
          padding: { top: 48, bottom: 48, left: side, right: side },
          maxZoom: 14,
        }),
      );
    });
  }

  // A move the controller makes itself (the opening fit, a pan to a row, a
  // resize) is no pan: the frame it came from stays, under whoever has the
  // focus in it. `holding` skips the frame's moveend, which is registered
  // first and so sees the flag before the `once` below clears it. The flag is
  // a token: a hold that interrupts another (Tab down rows whose pins are all
  // off the map) takes the flag over, and the interrupted move's moveend,
  // which fires inside the new move, frees nothing but its own token. A move
  // that never moves fires no moveend: drop the token on the next frame.
  // ponytail: `pad` holds with a plain true and puts back the token it found;
  // it ends its move before it returns, so it needs no token of its own.
  hold(move) {
    const token = (this.holding = Symbol('hold'));
    move?.();
    this.map.once('moveend', () => {
      if (this.holding === token) this.holding = false;
    });
    requestAnimationFrame(() => {
      if (this.holding === token && !this.map?.isMoving()) this.holding = false;
    });
  }

  // One frame for a swapped list: new data, and fit only when the person
  // didn't just pan (that reload would pull the camera back).
  track(target) {
    const point = this.point(target);
    if (!point.geom) return;
    this.pins.set(String(point.id), point);
    this.queuePins();
  }

  queuePins() {
    cancelAnimationFrame(this.syncing);
    this.syncing = requestAnimationFrame(() => {
      const follow = this.fromMap;
      this.fromMap = false;
      this.syncPins();
      this.showPins();
      if (!follow) this.refit();
    });
  }

  syncPins() {
    this.map?.getSource('pins')?.setData(this.collection());
  }

  // Every pin is a point of the glow, weighted by how loud it is.
  collection() {
    return {
      type: 'FeatureCollection',
      features: [...this.pins.values()].map((point) => ({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: point.geom },
        properties: { id: String(point.id), weight: this.weigh(point) },
      })),
    };
  }

  // A lone party is an ember, never nothing; the loudest burn. A log of the
  // boost (it runs to the millions), clamped.
  weigh(point) {
    return 0.55 + 0.45 * Math.min(1, Math.log10(1 + (point.boost || 0)) / 6);
  }

  // The Fun clouds (docs/MAPS.md): one heatmap under the labels, over the ground.
  mountClouds() {
    this.map.addSource('pins', { type: 'geojson', data: this.collection() });
    this.map.addLayer(this.cloudLayer(), this.map.getStyle().layers.find((layer) => layer.type === 'symbol')?.id);
  }

  cloudLayer() {
    return {
      id: 'fun-clouds',
      type: 'heatmap',
      source: 'pins',
      paint: this.glow(),
    };
  }

  // The glow is the theme's own primary, read at runtime (never a hex), from
  // transparent at the edge to the token at full at the core, with a brighter
  // step before it: light on the grey ground, a city from a plane at night.
  // Light needs more of it than dark. Wide and strong enough for neighbours to
  // merge into one blaze; it fades out over the zoom, never a hard cut.
  glow() {
    const [r, g, b] = this.token('--color-primary');
    const dark = getComputedStyle(document.documentElement).colorScheme !== 'light';
    // tokens:ok the colour is the primary token, read at runtime; `rgba` is only the syntax MapLibre takes.
    const shade = (alpha, white = 0) =>
      `rgba(${[r, g, b].map((c) => Math.round(c + (255 - c) * white)).join(',')},${alpha})`;
    // Dark burns: the core is the token at full, a brighter step before it. Light never reaches full
    // and never brightens (a lighter ring round a darker core is a donut): one soft, long falloff.
    const ramp = dark
      ? [0, shade(0), 0.15, shade(0.4), 0.4, shade(0.72), 0.7, shade(0.95, 0.35), 1, shade(1)]
      : [0, shade(0), 0.1, shade(0.14), 0.3, shade(0.32), 0.55, shade(0.52), 0.8, shade(0.7), 1, shade(0.84)];
    const intensity = dark ? [1.8, 3.4, 5] : [1.3, 2.3, 3.3];
    return {
      'heatmap-weight': ['get', 'weight'],
      'heatmap-intensity': ['interpolate', ['linear'], ['zoom'], 6, intensity[0], 12, intensity[1], 16, intensity[2]],
      'heatmap-radius': ['interpolate', ['linear'], ['zoom'], 6, 26, 10, 46, 13, 72, 16, 100],
      'heatmap-opacity': ['interpolate', ['linear'], ['zoom'], 13, 1, 15.5, 0],
      'heatmap-color': ['interpolate', ['linear'], ['heatmap-density'], ...ramp],
    };
  }

  // A css colour, as [r, g, b]: a canvas turns any of them (oklch included)
  // into the pixels MapLibre can take.
  token(name) {
    const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    const pad = document.createElement('canvas').getContext('2d', { willReadFrequently: true });
    pad.canvas.width = pad.canvas.height = 1;
    pad.fillStyle = value;
    pad.fillRect(0, 0, 1, 1);
    return [...pad.getImageData(0, 0, 1, 1).data].slice(0, 3);
  }

  still() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  // On the map the person sees, and whole: not under a host's sheet (`covered`),
  // and a flyer's width and height clear of the edges, so none is cut by them.
  inView(geom) {
    const { x, y } = this.map.project(geom);
    const box = this.map.getContainer();
    const side = Math.min(this.constructor.EDGE, box.clientWidth / 4);
    return x >= side && x <= box.clientWidth - side && y >= 120 && y <= box.clientHeight - this.covered - 12;
  }

  // Which pins are flyers: the first TOP by rank in view; close in (STREET),
  // every one. The rest are the glow only. A pin changing sides shrinks and
  // fades (`leave`, `enter`), it never pops.
  showPins() {
    if (!this.map) return;
    const { TOP, BIG, STREET } = this.constructor;
    const street = this.map.getZoom() >= STREET;
    const seen = [...this.pins.values()]
      .filter((point) => this.inView(point.geom))
      .sort((a, b) => (a.rank ?? Infinity) - (b.rank ?? Infinity));
    const shown = street ? seen : this.declutter(seen, TOP, BIG);
    const wanted = new Set(shown.map((point) => String(point.id)));
    shown.forEach((point, rank) => {
      const id = String(point.id);
      let marker = this.markers.get(id);
      if (marker) this.restore(marker);
      else {
        marker = this.leaf(point).addTo(this.map);
        this.markers.set(id, marker);
        this.enter(marker);
      }
      marker.setLngLat(point.geom);
      marker.getElement().toggleAttribute('data-big', rank < BIG);
    });
    for (const id of [...this.markers.keys()]) if (!wanted.has(id)) this.leave(id);
    this.fan();
  }

  // The loudest first, each only where no promoted one already stands: a pin whose box
  // would meet a placed one stays in the cloud (it shrinks into it, it never piles on).
  // Except on the same point: that one comes in with the placed (it counts toward the
  // top), and `fan` spreads the pile, so no night is left under another's pin.
  declutter(ranked, top, big) {
    const placed = [];
    const boxes = [];
    const stood = new Set();
    for (const point of ranked) {
      if (placed.length === top) break;
      const box = this.box(point, placed.length < big);
      if (!stood.has(this.pile(point)) && boxes.some((one) => this.meet(one, box))) continue;
      placed.push(point);
      boxes.push(box);
      stood.add(this.pile(point));
    }
    return placed;
  }

  // Where its marker stands, from its anchor (bottom centre): a flyer by its size, a pill by its words.
  box(point, big) {
    const { x, y } = this.map.project(point.geom);
    const flyer = point.at !== point.name && (point.poster || point.cover);
    const width = flyer
      ? big
        ? 112
        : 80
      : this.markers.get(String(point.id))?.getElement().offsetWidth || 7 * String(point.at).length + 24;
    const height = flyer ? (big ? 124 : 92) : 28;
    return [x - width / 2 - 4, y - height - 4, x + width / 2 + 4, y + 4];
  }

  meet(a, b) {
    return a[0] < b[2] && b[0] < a[2] && a[1] < b[3] && b[1] < a[3];
  }

  enter(marker) {
    if (this.still()) return;
    const el = marker.getElement();
    this.fade(el, marker, '0', '0.4');
    el.getBoundingClientRect();
    requestAnimationFrame(() => this.fade(el, marker, '1', ''));
  }

  // The transition is on for the fade only: a pin that is back keeps none of it.
  fade(el, marker, opacity, scale) {
    const { FADE } = this.constructor;
    clearTimeout(el.fading);
    el.style.transition = `opacity ${FADE}ms, scale ${FADE}ms`;
    el.style.scale = scale;
    marker.setOpacity(opacity);
    if (opacity === '1') el.fading = setTimeout(() => (el.style.transition = ''), FADE);
  }

  restore(marker) {
    const el = marker.getElement();
    if (!el.dataset.leaving) return;
    delete el.dataset.leaving;
    this.fade(el, marker, '1', '');
  }

  leave(id) {
    const marker = this.markers.get(id);
    const el = marker.getElement();
    if (el.dataset.leaving) return;
    if (this.still()) return this.drop(id);
    const token = (this.leaving = (this.leaving || 0) + 1);
    el.dataset.leaving = String(token);
    this.fade(el, marker, '0', '0.4');
    setTimeout(() => el.dataset.leaving === String(token) && this.drop(id), this.constructor.FADE);
  }

  // A removed node gets no mouseleave or blur: what the pointer or the focus lit goes with it.
  drop(id) {
    const marker = this.markers.get(id);
    if (marker?.getElement().matches(':hover, :focus')) {
      this.lit?.remove();
      this.row(id)?.classList.remove('bg-base-200');
    }
    marker?.remove();
    this.markers.delete(id);
    this.fan();
  }

  // A row whose party is only glow: its spot lights, a ring at the point.
  ember(point, on) {
    this.spot?.remove();
    this.spot = null;
    if (!on || !point.geom || !this.map) return;
    const el = document.createElement('span');
    el.dataset.ember = '1';
    el.className = 'block size-8 rounded-full bg-primary/40 ring-2 ring-primary shadow-lg';
    this.spot = new maplibregl.Marker({ element: el, anchor: 'center' }).setLngLat(point.geom).addTo(this.map);
  }

  // The classic green sweep (a bar from the visible middle, one clockwise
  // turn, a faint tail trailing, a few range rings), over the map and under
  // the pins, then gone. The keyframes are `animate-hunt` (the app's css).
  sweepOnce() {
    const box = this.map.getContainer();
    const w = box.clientWidth;
    const h = box.clientHeight - this.covered;
    const el = document.createElement('div');
    el.setAttribute('aria-hidden', 'true');
    el.dataset.sweep = '1';
    el.className = [
      'absolute -translate-1/2 aspect-square rounded-full pointer-events-none motion-reduce:hidden animate-hunt',
      'bg-[conic-gradient(from_0deg,transparent_0deg,transparent_250deg,color-mix(in_oklab,var(--color-success)_22%,transparent)_356deg,var(--color-success)_359.4deg,transparent_360deg),repeating-radial-gradient(circle,transparent_0_calc(12rem-1px),color-mix(in_oklab,var(--color-success)_35%,transparent)_calc(12rem-1px)_12rem)]',
    ].join(' ');
    el.style.cssText = `left:${w / 2}px;top:${h / 2}px;width:${Math.hypot(w, h) * 2}px`;
    el.addEventListener('animationend', () => el.remove());
    // Right after the canvas: the markers are added after it, so they sit over.
    this.map.getCanvas().after(el);
  }

  leaf(point) {
    const flyer = point.at && point.at !== point.name && (point.poster || point.cover);
    const element = flyer ? this.flyerPin(point) : point.at ? this.labelPin(point) : this.pin(point);
    const marker = new maplibregl.Marker({
      element,
      anchor: flyer || point.at ? 'bottom' : 'center',
    }).setLngLat(point.geom);
    if (!flyer && !point.at) marker.setPopup(new maplibregl.Popup({ offset: 28 }).setHTML(this.popup(point)));
    return marker;
  }

  // Where a party stands, as one word: parties that share a venue or a city
  // centroid share it.
  pile(point) {
    return point.geom.map((n) => Number(n).toFixed(5)).join(',');
  }

  // Parties that share a point sit on one pixel. Spread every one of them,
  // the loudest in the middle (docs/MAPS.md § Same point).
  fan() {
    const piles = new Map();
    for (const [id, marker] of this.markers) {
      const point = this.pins.get(id);
      if (!point?.geom || marker.getElement().dataset.leaving) continue;
      const key = this.pile(point);
      if (!piles.has(key)) piles.set(key, []);
      piles.get(key).push(id);
    }
    for (const ids of piles.values()) {
      ids.sort((a, b) => (this.pins.get(a).rank ?? 1e9) - (this.pins.get(b).rank ?? 1e9));
      const n = ids.length;
      // Centre out: seat 0 is the loudest's, in the middle, the next ones to either side of it.
      const seats = [...ids.keys()].sort((a, b) => Math.abs(a - (n - 1) / 2) - Math.abs(b - (n - 1) / 2) || a - b);
      ids.forEach((id, rank) => this.pose(id, n > 1 ? seats[rank] : -1, n, rank));
    }
  }

  // slot -1 sits on its point; else seat `slot` of `n`, as wide as the screen allows.
  pose(id, slot, n, rank = 0) {
    const marker = this.markers.get(id);
    if (!marker) return;
    const el = marker.getElement();
    if (slot < 0) {
      el.style.rotate = '';
      delete el.dataset.z;
      el.style.zIndex = '';
      marker.setOffset([0, 0]);
      return;
    }
    // Name pills stack, loudest at the point: side by side their words would cover each other.
    if (el.classList.contains('badge')) {
      marker.setOffset([0, rank * -28]);
      el.style.rotate = '';
      el.dataset.z = String(4 - Math.min(rank, 3));
      el.style.zIndex = el.dataset.z;
      return;
    }
    const shift = slot - (n - 1) / 2;
    const unit = shift / ((n - 1) / 2);
    marker.setOffset([unit * Math.min(32 * (n - 1), 128), Math.abs(unit) * -Math.min(9 * (n - 1), 36)]);
    el.style.rotate = `${unit * Math.min(7 * (n - 1), 28)}deg`;
    el.dataset.z = String(4 - Math.min(3, Math.round(Math.abs(shift))));
    el.style.zIndex = el.dataset.z;
  }

  point(target) {
    return JSON.parse(target.dataset.point);
  }

  erase(target) {
    const { id } = this.point(target);
    this.markers.get(id)?.remove();
    this.markers.delete(id);
  }

  draw(target, bubble) {
    const point = this.point(target);
    if (this.markers.has(point.id) || !point.geom) return;

    // A pin with an `at` is its label; the popup opens on hover.
    const label = !bubble && point.at;
    const element = bubble ? this.bubble(point) : label ? this.labelPin(point) : this.pin(point);
    const marker = new maplibregl.Marker({
      element,
      anchor: label ? 'bottom' : 'center',
    }).setLngLat(point.geom);
    if (!bubble && !label) marker.setPopup(new maplibregl.Popup({ offset: 28 }).setHTML(this.popup(point)));
    this.markers.set(point.id, marker.addTo(this.map));
    if (bubble) this.spread();
  }

  // Bubbles never sit under one another, and only those the map shows are Tab
  // stops (docs/MAPS.md § Spots). Offsets are screen px from the point, so
  // zoomed in they settle back on it.
  spread() {
    if (!this.map) return;
    const bounds = this.map.getBounds();
    const all = [...this.markers.values()]
      .filter((marker) => marker.getElement().dataset.spot)
      .map((marker) => {
        const el = marker.getElement();
        const { x, y } = this.map.project(marker.getLngLat());
        return { marker, el, x, y, r: el.offsetWidth / 2 + 2 };
      });
    for (let pass = 0; pass < 8; pass++) {
      for (let i = 0; i < all.length; i++) {
        for (let j = i + 1; j < all.length; j++) {
          const [a, b] = [all[i], all[j]];
          let [dx, dy] = [b.x - a.x, b.y - a.y];
          let gap = Math.hypot(dx, dy);
          const least = a.r + b.r;
          if (gap >= least) continue;
          if (!gap) [dx, dy, gap] = [1, 0, 1];
          const push = (least - gap) / 2;
          a.x -= (push * dx) / gap;
          a.y -= (push * dy) / gap;
          b.x += (push * dx) / gap;
          b.y += (push * dy) / gap;
        }
      }
    }
    for (const one of all) {
      const home = this.map.project(one.marker.getLngLat());
      one.marker.setOffset([one.x - home.x, one.y - home.y]);
      if (bounds.contains(one.marker.getLngLat())) one.el.removeAttribute('tabindex');
      else one.el.tabIndex = -1;
    }
  }

  // A pin wearing its own flyer, ringed in the "now" colour when `soon`.
  pin(point) {
    const el = document.createElement('div');
    const ring = point.soon ? 'ring-secondary' : 'ring-base-100';
    el.className = `size-11 rounded-field bg-base-300 bg-cover bg-center shadow-md cursor-pointer ring-2 ${ring} transition-[scale] duration-200`;
    el.style.backgroundImage = `url(${point.cover})`;
    el.title = point.name;
    return el;
  }

  // The hour was the whole pin. The flyer is the pin; the hour sits on it.
  flyerPin(point) {
    const el = document.createElement('button');
    el.type = 'button';
    el.className = 'indicator cursor-pointer origin-bottom transition-[scale] duration-200';
    el.title = point.name;
    el.setAttribute('aria-label', `${point.at} ${point.name}`);

    const time = document.createElement('span');
    time.className = `indicator-item indicator-bottom indicator-center badge badge-sm font-semibold tabular-nums shadow-md ${point.soon ? 'badge-secondary' : 'badge-neutral'}`;
    time.textContent = point.at;

    const card = document.createElement('span');
    card.className = `block size-20 in-data-[big]:size-28 rounded-field bg-base-100 bg-cover bg-center shadow-2xl border-2 ${point.soon ? 'border-secondary' : 'border-base-content/30'}`;
    card.style.backgroundImage = `url("${point.poster || point.cover}")`;
    card.setAttribute('aria-hidden', 'true');

    el.append(time, card);
    this.bindPin(el, point);
    return el;
  }

  // A pin by its label (an hour, or its own name): a small pill, the popup
  // above it only while lit. Clicking it finds its row, or opens the link
  // when the list is hidden.
  labelPin(point) {
    const el = document.createElement('button');
    el.type = 'button';
    el.className = `badge badge-sm block max-w-48 truncate h-auto min-h-5 py-0.5 font-semibold tabular-nums shadow-md cursor-pointer ${point.soon ? 'badge-secondary' : 'border-base-content/20 bg-base-100'}`;
    el.setAttribute('aria-label', point.at); // a pill names its place already
    el.textContent = point.at;
    this.bindPin(el, point);
    return el;
  }

  bindPin(el, point) {
    for (const [type, on] of [
      ['mouseenter', true],
      ['mouseleave', false],
      ['focus', true],
      ['blur', false],
    ]) {
      el.addEventListener(type, () => this.light(point, on));
    }
    el.addEventListener('click', () => {
      const row = this.row(point.id);
      if (!row?.checkVisibility()) return Turbo.visit(point.link);
      row.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      row.querySelector('a')?.focus({ preventScroll: true });
    });
  }

  // Hovering or tabbing a row lights its pin, panning to it if it is off the
  // map; the pin lights its row back.
  spotlight({ currentTarget, type }) {
    const point = this.point(currentTarget);
    const on = type === 'mouseenter' || type === 'focusin';
    this.light(point, on);
    if (on && point.geom && this.map && !this.map.getBounds().contains(point.geom)) {
      this.hold(() => this.map.panTo(point.geom));
    }
  }

  light(point, on) {
    // The ring is the hover's: it goes with it, though the pan promoted the party to a pin meanwhile.
    if (!on) this.ember(point, false);
    const el = this.markers.get(point.id)?.getElement();
    // Only glow: no marker, so the spot lights (a row is never dead).
    if (this.cloudsValue && (!el || el.dataset.leaving)) {
      this.ember(point, on);
      if (point.at) this.hover(point, on);
    } else if (el) {
      el.style.zIndex = on ? 20 : el.dataset.z || '';
      if (el.classList.contains('origin-bottom')) el.classList.toggle('scale-110', on);
      if (point.at) this.hover(point, on);
      else {
        el.classList.toggle('scale-125', on);
        el.classList.toggle('ring-primary', on);
      }
    }
    this.row(point.id)?.classList.toggle('bg-base-200', on);
  }

  // One popup for whichever is lit; no close button, the mouse leaving is
  // the close. It takes no focus (its link would wear the focus ring) and its
  // tip stays centred: above the pill, or below it when the pill is too near
  // the top. MapLibre's own pick slides to a corner.
  hover(point, on) {
    this.lit?.remove();
    if (!on || !this.map) return;
    const flyer = point.at !== point.name && (point.poster || point.cover);
    this.lit = new maplibregl.Popup({
      ...this.above(point, flyer ? -(this.markers.get(point.id)?.getElement().offsetHeight || 64) : -26),
      closeButton: false,
      closeOnClick: false,
      focusAfterOpen: false,
    })
      .setLngLat(point.geom)
      .setHTML(this.popup(point))
      .addTo(this.map);
  }

  // Above the pill (`up` px, its height), or below it when the pill is too
  // near the top: either way the tip stays centred.
  above(point, up) {
    const y = this.map.project(point.geom).y;
    const anchor = y < 280 ? 'top' : 'bottom';
    return { anchor, offset: { top: [0, 8], bottom: [0, up] } };
  }

  row(id) {
    return this.pinTargets.find((one) => this.point(one).id === id);
  }

  // A city or a venue: how many are inside it, sized by how many. Clicking
  // it is the zoom to the scale the server named, or, with a `visit`, that
  // page (um's /doar: a town's list).
  bubble(spot) {
    const size = Math.min(56, 28 + String(spot.count).length * 8);
    const el = document.createElement('button');
    el.type = 'button';
    el.className =
      'flex items-center justify-center rounded-full bg-primary text-primary-content text-sm font-semibold tabular-nums shadow-md ring-2 ring-base-100 leading-none cursor-pointer';
    el.style.width = `${size}px`;
    el.style.height = `${size}px`;
    el.innerHTML = `<span>${spot.count}</span>`;
    el.title = spot.name;
    // The count is the visible text, so the name holds it (WCAG 2.5.3).
    el.setAttribute('aria-label', `${spot.name} · ${spot.count}`);
    el.dataset.spot = '1';
    el.addEventListener('click', () =>
      spot.visit ? Turbo.visit(spot.visit) : this.map.flyTo({ center: spot.geom, zoom: spot.zoom || 14 }),
    );
    return el;
  }

  // Panning is continuous and each move is a request, so it waits for a
  // pause. Everything else in the query string is a filter somebody chose —
  // it survives the pan; only where and how close is ours to rewrite.
  refresh() {
    clearTimeout(this.pending);
    this.pending = setTimeout(() => {
      if (!this.map) return;
      // The reload that follows is the pan's, so it must not refit.
      this.fromMap = true;
      const { lng, lat } = this.map.getCenter();
      const url = new URL(window.location.href);
      url.searchParams.set('lng', lng.toFixed(3));
      url.searchParams.set('lat', lat.toFixed(3));
      url.searchParams.set('zoom', Math.round(this.map.getZoom()));
      Turbo.visit(url.toString(), {
        action: 'replace',
        frame: this.frameValue,
      });
    }, 500);
  }

  // Every word here is somebody's record (a promoter names a Fun): escaped,
  // so a name with markup in it reads as those characters (docs/MAPS.md).
  popup(point) {
    return `
      <a href="${esc(point.link)}" class="flex w-56 flex-col gap-2" target="_blank" rel="noopener noreferrer">
        ${point.cover ? `<img src="${esc(point.cover)}" alt="${esc(point.name)}" class="aspect-video w-full object-cover rounded-field">` : ''}
        <div>
          <h3 class="font-semibold text-base leading-snug">${esc(point.name)}</h3>
          <p class="text-sm text-base-content/70">${esc(point.day)}</p>
          ${point.value ? `<p class="text-sm text-base-content/70">${esc(point.value)}</p>` : ''}
        </div>
      </a>
    `;
  }

  async locate() {
    if (!navigator.geolocation) return this.constructor.NOWHERE;

    try {
      const at = await new Promise((ok, no) => navigator.geolocation.getCurrentPosition(ok, no));
      return [at.coords.longitude, at.coords.latitude];
    } catch {
      return this.constructor.NOWHERE;
    }
  }
}
